import React, { useState, useRef, useEffect } from 'react';
import { User, Clock, Heart, Reply, MoreHorizontal, Trash2 } from 'lucide-react';
import { clipboardService } from '../services/api';
import toast from 'react-hot-toast';

const Comment = ({ comment, itemId, onDelete, currentUserId }) => {
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu on click outside
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
    setLikeCount(prev => isLiked ? prev - 1 : prev + 1);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this comment?')) return;
    setDeleting(true);
    try {
      await clipboardService.deleteComment(itemId, comment.id);
      toast.success('Comment deleted');
      if (onDelete) onDelete(comment.id);
    } catch (error) {
      toast.error('Failed to delete comment');
    } finally {
      setDeleting(false);
      setMenuOpen(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 p-6 group comment-card">
      {/* Comment Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-sm">
            <User className="h-5 w-5 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-900">
              {comment.author_name || 'Anonymous'}
            </span>
            <div className="flex items-center space-x-2">
              <Clock className="h-3 w-3 text-gray-400" />
              <span className="text-xs text-gray-500 font-medium">
                {formatDate(comment.created_at)}
              </span>
            </div>
          </div>
        </div>
        
        {/* Action Menu */}
        <div className="relative" ref={menuRef}>
          <button
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 rounded-full hover:bg-gray-100"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="More options"
          >
            <MoreHorizontal className="h-4 w-4 text-gray-400" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-2 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              {currentUserId === comment.user_id && (
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Comment Content */}
      <div className="mb-4">
        <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
          {comment.content}
        </p>
      </div>

      {/* Comment Actions */}
      <div className="flex items-center space-x-6 pt-3 border-t border-gray-50">
        <button
          onClick={handleLike}
          className={`flex items-center space-x-2 text-sm font-medium transition-all duration-200 ${
            isLiked 
              ? 'text-red-500 hover:text-red-600' 
              : 'text-gray-500 hover:text-red-500'
          }`}
        >
          <Heart className={`h-4 w-4 transition-all duration-200 ${
            isLiked ? 'fill-current' : ''
          }`} />
          <span>{likeCount}</span>
        </button>
        
        <button className="flex items-center space-x-2 text-sm font-medium text-gray-500 hover:text-blue-600 transition-colors duration-200">
          <Reply className="h-4 w-4" />
          <span>Reply</span>
        </button>
      </div>
    </div>
  );
};

export default Comment; 